import debugLib from 'debug';

const debug = debugLib('Mixpanel');

const MSG_ERROR_NOT_LOAD = 'Mixpanel library not loaded';
const PREFIX_CLICK = 'Click';
const PREFIX_PAGEVIEW = 'View';
const PREFIX_SITE = 'DB:';

/**
 * Identify if mixpanel load correctly
 * @return {Boolean}
 */
const isMixpaneLoaded = function() {
  return window.mixpanel && typeof window.mixpanel.get_property === 'function';
};

const ReactI13NMixpanel = {
  name: 'mixpanel',
  eventHandlers: {
    /**
     * Page view event handler
     * The event format will be 'DB: View {path}'
     * @param  {Object} payload payload passing from pageview event
     * @param  {String} payload.label page label
     * @param  {Object} payload.params other params to pass in @optional
     */
    pageview: function handlePageview({ label, params = {} }) {
      if (!isMixpaneLoaded()) {
        debug(MSG_ERROR_NOT_LOAD);
        return;
      }
      const eventLabel = `${PREFIX_SITE} ${PREFIX_PAGEVIEW} ${label}`;
      debug('Fire pageview', eventLabel, params);
      mixpanel.track(eventLabel, { params });
    },
    /**
     * Log event handler (generic logger for any action)
     * @param  {Object} payload payload passing from log event
     */
    log: function handleLog({ i13nNode, label, params = {} }) {
      if (!isMixpaneLoaded()) {
        debug(MSG_ERROR_NOT_LOAD);
        return;
      }
      if (i13nNode) {
        const model = i13nNode.getMergedModel();
        // passing in a 'path' property on i13nModel for 'log' event does not
        // come through on the model as it does for 'click' event, hence why
        // we have to pass it in via params
        const path = params.path || model.path || 'root';
        const eventLabel = `${PREFIX_SITE} ${path}: ${label}`;

        debug('Fire custom event', eventLabel, params);
        mixpanel.track(eventLabel, { params });
      }
    },
    /**
     * Click event handler
     * The event format should be like 'DB: {path}: Click: {item}''
     * @param  {Object} payload payload passing from click event
     */
    click: function handleClickEvent({ i13nNode, label, params }) {
      if (!isMixpaneLoaded()) {
        debug(MSG_ERROR_NOT_LOAD);
        return;
      }

      if (i13nNode) {
        const model = i13nNode.getMergedModel();
        const { path = 'root' } = model;
        const customLabel = model.label || label;
        const customParams = params || model.params || {};

        const eventLabel = `${PREFIX_SITE} ${path}: ${PREFIX_CLICK} ${customLabel}`;

        debug('Fire click beacon', eventLabel, customParams);
        mixpanel.track(eventLabel, { params: customParams });
      }
    },
    /**
     * Register event handler
     * @param  {Object} payload payload passing from register event
     * @param  {String} payload.appId app ID to register
     * @param  {String} payload.bmp bmp cookie
     * @param  {String} payload.name user name
     * @param  {String} payload.email user email
     * @param  {String} payload.dashboard_user_id user's dashboard id
     * @param  {String} payload.accessControl user's access control settings for this app
     */
    register: function handleRegister({
      bmp,
      email,
      name,
      dashboard_user_id,
      accessControl
    }) {
      if (!isMixpaneLoaded()) {
        debug(MSG_ERROR_NOT_LOAD);
        return;
      }
      const { app_id: appId, organization_id: orgId } = window.App.context.view;

      if (
        mixpanel.get_property('app') !== appId ||
        mixpanel.get_property('org') !== orgId
      ) {
        debug(
          `Register app:${appId ? appId : null}, org:${orgId ? orgId : null}`
        );
        mixpanel.register({
          app: appId ? appId : null,
          org: orgId ? orgId : null,
          bmp: bmp
        });
      }

      /* We want to register permissions each time the app loads even if the
       * other register properties have been set already
       */
      const permissions = Object.keys(accessControl).reduce((acc, resource) => {
        if (acc && accessControl && accessControl[resource]) {
          acc[resource] = accessControl[resource].permission;
        }
        return { ...acc };
      }, {});
      mixpanel.register({ permissions });

      const distinctId = mixpanel.get_distinct_id();
      if (!distinctId) {
        debug('Register new person', name, email);
        mixpanel.people.set({
          $email: email,
          $name: name
        });
      }
      /* Even if a user already has a distinctId set, we want to reset
       * it to be their dashboard_user_id, as we have used other things like
       * email in this field in the past and we want to standardize across
       * all users.
       */
      mixpanel.identify(dashboard_user_id);
    }
  }
};

export function track(message, options) {
  try {
    window.mixpanel && window.mixpanel.track(message, options);
  } catch (err) {
    console.log(err);
  }
}

export default ReactI13NMixpanel;
